import React, { useEffect, useMemo, useState } from 'react';
import { ListGroup, Spinner } from 'react-bootstrap';
import { FaArchive, FaFileSignature } from 'react-icons/fa';
import './ArticleList.css';
import ArticleCard from './ArticleCard';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { ROLES } from '../../platform/constants';
import { effectiveRole, isTenantAdminForTenant } from '../../platform/membershipPermissions';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { useUnreadItemIds } from '../../platform/lastRead/useUnreadItemIds';
import {
  ARTICLE_ESSENTIAL_SECTION_TITLE,
  groupArticles,
  resolveArticleEssentialSectionSubtitle,
  resolveArticleListGroupSubtitle,
  resolveArticleMiddleGridColumns,
} from '../../services/articleListGroups';
import {
  buildArticlesGroupedListQuery,
  deleteArticle,
  mapArticleFromFirestore,
} from '../../services/articlesService';
import { onSnapshot } from 'firebase/firestore';

import { filterByActiveTenant } from '../../services/tenantContentFilter';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import {
  PREMIUM_MODULE_IDS,
  premiumListClassName,
  showListNavigateHint,
} from '../../platform/contentListVisual';

function ArticleTileSection({
  title,
  subtitle,
  count,
  icon: Icon,
  variant = 'default',
  compactSubtitle = false,
  inGrid = false,
  listClassName = 'article-list platform-content-list',
  children,
}) {
  const header = (
    <header className="article-list-section__header">
      {Icon && <Icon className="article-list-section__icon" aria-hidden="true" />}
      <div className="article-list-section__heading">
        <h2 className="article-list-section__title">{title}</h2>
        {subtitle && (
          <p
            className={`article-list-section__subtitle${compactSubtitle || inGrid ? ' article-list-section__subtitle--compact' : ''}${inGrid ? ' article-list-section__subtitle--grid' : ''}`}
          >
            {subtitle}
          </p>
        )}
      </div>
      {typeof count === 'number' && (
        <span className="article-list-section__count">{count}</span>
      )}
    </header>
  );

  return (
    <section className={`article-list-section article-list-section--${variant}${inGrid ? ' article-list-section--grid' : ''}`}>
      {inGrid ? (
        <div className="article-list-section__panel">
          {header}
          <ListGroup className={`${listClassName} article-list--${variant}`}>
            {children}
          </ListGroup>
        </div>
      ) : (
        <>
          {header}
          <ListGroup className={`${listClassName} article-list--${variant}`}>
            {children}
          </ListGroup>
        </>
      )}
    </section>
  );
}

function ArticleList({ onSelectArticle }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const { user, loading: userLoading } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();

  const unreadArticleIds = useUnreadItemIds(LAST_READ_MODULES.ARTICLES, articles);

  const { essential, sections } = useMemo(
    () => groupArticles(articles, config),
    [articles, config],
  );

  const middleSections = useMemo(
    () => sections.filter(({ group }) => group.id !== 'archive'),
    [sections],
  );

  const archiveSection = useMemo(
    () => sections.find(({ group }) => group.id === 'archive'),
    [sections],
  );

  const gridColumns = resolveArticleMiddleGridColumns(middleSections.length);

  const canDeleteArticle = (article) => {
    if (isTenantAdminForTenant(memberships, tenantId, user)) {
      return true;
    }
    const role = effectiveRole(memberships, tenantId, user);
    return role === ROLES.LOCAL_ADMIN && article.uid === user?.uid;
  };

  useEffect(() => {
    if (userLoading || !user || !tenantId) {
      setArticles([]);
      setLoading(userLoading);
      return undefined;
    }

    setLoading(true);
    const listQuery = buildArticlesGroupedListQuery(user, tenantId, memberships);
    const unsubscribe = onSnapshot(listQuery, (querySnapshot) => {
      const items = filterByActiveTenant(
        querySnapshot.docs.map(mapArticleFromFirestore),
        tenantId,
      );
      setArticles(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching articles:', error);
      setArticles([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userLoading, user, tenantId, memberships]);

  const confirm = useConfirm();
  const toast = useToast();
  const showNavigateHint = showListNavigateHint(PREMIUM_MODULE_IDS.ARTICLES);
  const listClassName = premiumListClassName(
    PREMIUM_MODULE_IDS.ARTICLES,
    'article-list platform-content-list',
  );
  const wrapperClassName = [
    'article-list-wrapper',
    'platform-content-list-wrapper',
    showNavigateHint && 'platform-content-list-wrapper--card-stack',
  ].filter(Boolean).join(' ');

  const handleDelete = async (article) => {
    const confirmed = await confirm({
      title: 'Izbriši obrazec',
      message: 'Ali ste prepričani, da želite izbrisati ta obrazec?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteArticle(article);
      toast.deleted('Obrazec je bil izbrisan.');
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Obrazca ni bilo mogoče izbrisati.');
    }
  };

  const renderTile = (article, variant = 'default', compact = false) => (
    <ArticleCard
      key={article.id}
      article={article}
      tenantId={tenantId}
      isUnread={unreadArticleIds.includes(article.id)}
      canDelete={canDeleteArticle(article)}
      reserveDeleteAction={Boolean(user?.uid)}
      variant={variant}
      compact={compact}
      onSelectArticle={onSelectArticle}
      onDelete={handleDelete}
    />
  );

  if (loading || userLoading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (!articles.length) {
    return <div className="platform-content-list-empty">Ni obrazcev.</div>;
  }

  return (
    <div className={wrapperClassName}>
      {essential.length > 0 && (
        <ArticleTileSection
          title={ARTICLE_ESSENTIAL_SECTION_TITLE}
          subtitle={resolveArticleEssentialSectionSubtitle(config)}
          count={essential.length}
          icon={FaFileSignature}
          variant="featured"
          listClassName={listClassName}
        >
          {essential.map((article) => renderTile(article, 'featured'))}
        </ArticleTileSection>
      )}

      {middleSections.length > 0 && (
        <div className={`article-list-groups article-list-groups--cols-${gridColumns}`}>
          {middleSections.map(({ group, articles: groupArticles }) => (
            <ArticleTileSection
              key={group.id}
              title={group.label}
              subtitle={resolveArticleListGroupSubtitle(group)}
              count={groupArticles.length}
              variant="default"
              compactSubtitle
              inGrid
              listClassName={listClassName}
            >
              {groupArticles.map((article) => renderTile(article, 'default', true))}
            </ArticleTileSection>
          ))}
        </div>
      )}

      {archiveSection && (
        <ArticleTileSection
          title={archiveSection.group.label}
          subtitle={resolveArticleListGroupSubtitle(archiveSection.group)}
          count={archiveSection.articles.length}
          icon={FaArchive}
          variant="archive-banner"
          listClassName={listClassName}
        >
          {archiveSection.articles.map((article) => renderTile(article, 'archive'))}
        </ArticleTileSection>
      )}
    </div>
  );
}

export default ArticleList;
